"use client"
import { useState, useEffect, type FormEvent } from "react"
import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../../shared/functions/Context"

import MODAL_NAMES from "../../../dialogs/ModalName"
import { type IValue, defaultValue } from "../../../../shared/models/three-sixty-feedback-models/Values"
import { hideModalFromId } from "../../../../shared/functions/ModalShow"

const EditValueModal = observer(() => {
  const { api, store } = useAppContext()
  const me = store.auth.meJson
  const [value, setValue] = useState<IValue>(defaultValue)
  const [loading, setLoading] = useState(false)
  const [statements, setStatements] = useState<{ id: string; statement: string; rating: number }[]>([])

  const handleStatementChange = (index: number, statement: string) => {
    const newStatements = [...statements]
    newStatements[index].statement = statement
    setStatements(newStatements)
  }

  const deleteStatement = (index: number) => {
    const newStatements = statements.filter((_, i) => i !== index)
    setStatements(newStatements)
  }

  const addStatement = () => {
    const newStatements = [...statements, { id: new Date().toISOString(), statement: "", rating: 0 }]
    setStatements(newStatements)
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)

    const updatedValue = {
      ...value,
      statements: statements.map(({ id, statement, rating }) => ({ id, statement, rating })),
    }

    if (!me) return
    await update(updatedValue)
    setLoading(false)
    onCancel()
  }

  const update = async (value: IValue) => {
    try {
      await api.value.update(value)
    } catch (error) {
      console.error("Error updating value:", error)
    }
  }

  useEffect(() => {
    if (store.value.selected) {
      setValue({ ...store.value.selected })
      setStatements(
        store.value.selected.statements.map((statement) => ({
          id: statement.statement,
          statement: statement.statement,
          rating: statement.rating,
        })),
      )
    } else {
      setValue({ ...defaultValue })
      setStatements([])
      hideModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.EDIT_VALUE_MODAL)
    }
  }, [store.value, store.value.selected])

  const onCancel = () => {
    setValue({ ...defaultValue })
    setStatements([])
    hideModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.EDIT_VALUE_MODAL)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-green-100 text-green-600">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="m20.749 12 1.104-1.908a1 1 0 0 0-.365-1.366l-1.91-1.104v-2.2a1 1 0 0 0-1-1h-2.199l-1.103-1.909a1.008 1.008 0 0 0-.607-.466.993.993 0 0 0-.759.1L12 3.251l-1.91-1.105a1 1 0 0 0-1.366.366L7.62 4.422H5.421a1 1 0 0 0-1 1v2.199l-1.91 1.104a.998.998 0 0 0-.365 1.367L3.25 12l-1.104 1.908a1.004 1.004 0 0 0 .364 1.367l1.91 1.104v2.199a1 1 0 0 0 1 1h2.2l1.104 1.91a1.01 1.01 0 0 0 .866.5c.174 0 .347-.046.501-.135l1.908-1.104 1.91 1.104a1.001 1.001 0 0 0 1.366-.365l1.103-1.91h2.199a1 1 0 0 0 1-1v-2.199l1.91-1.104a1 1 0 0 0 .365-1.367L20.749 12zM9.499 6.99a1.5 1.5 0 1 1-.001 3.001 1.5 1.5 0 0 1 .001-3.001zm.3 9.6-1.6-1.199 6-8 1.6 1.199-6 8zm4.7.4a1.5 1.5 0 1 1 .001-3.001 1.5 1.5 0 0 1-.001 3.001z"></path>
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Edit Value</h3>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9.172 16.242 12 13.414l2.828 2.828 1.414-1.414L13.414 12l2.828-2.828-1.414-1.414L12 10.586 9.172 7.758 7.758 9.172 10.586 12l-2.828 2.828z"></path>
              <path d="M12 22c5.514 0 10-4.486 10-10S17.514 2 12 2 2 6.486 2 12s4.486 10 10 10zm0-18c4.411 0 8 3.589 8 8s-3.589 8-8 8-8-3.589-8-8 3.589-8 8-8z"></path>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="value-name" className="text-sm font-medium text-gray-700">
              Value Name
            </label>
            <input
              id="value-name"
              type="text"
              placeholder="Enter value name"
              value={value?.valueName}
              onChange={(e) =>
                setValue({
                  ...value,
                  valueName: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700">Statements</label>
            <div className="space-y-2">
              {statements.map((statement, index) => (
                <div key={statement.id} className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder={`Enter Statement ${index + 1}`}
                    value={statement.statement}
                    onChange={(e) => handleStatementChange(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => deleteStatement(index)}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M5 20a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8h2V6h-4V4a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v2H3v2h2zM9 4h6v2H9zM8 8h9v12H7V8z"></path>
                      <path d="M9 10h2v8H9zm4 0h2v8h-2z"></path>
                    </svg>
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={addStatement}
              disabled={!value.valueName.trim()}
              className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add Statement
            </button>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!value.valueName.trim() || statements.some((s) => !s.statement.trim()) || loading}
              className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
})

export default EditValueModal
